import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import client from "@/api/client";
import { Award, Download, Link as LinkIcon, Copy, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Certificate() {
  const [busy, setBusy] = useState(false);
  const [cert, setCert] = useState(null);

  useEffect(() => {
    // Best-effort check: try downloading a HEAD-like tiny GET; if 400 no cert yet.
    // Instead we use a lightweight metadata endpoint: try a GET and inspect only the header.
    // We simply hit /verify/... after each download. On initial page load we ask the API for the
    // logged-in user's cert if it exists.
    client.get("/certificate/mine", { validateStatus: () => true })
      .then((r) => { if (r.status === 200) setCert(r.data); });
  }, []);

  const dl = async () => {
    setBusy(true);
    try {
      const r = await client.get("/certificate", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "boardroom-certificate.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
      // fetch cert meta
      const m = await client.get("/certificate/mine", { validateStatus: () => true });
      if (m.status === 200) setCert(m.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to issue certificate yet");
    } finally { setBusy(false); }
  };

  const shareUrl = cert ? `${window.location.origin}/verify/${cert.code}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Verify link copied to clipboard");
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-3xl mx-auto text-center py-10" data-testid="certificate-page">
        <div className="crest justify-center mb-4">
          <span className="text-[#D4AF37] text-2xl">◇</span>
          <span>The Boardroom</span>
        </div>
        <p className="overline">Award</p>
        <h1 className="font-display text-6xl md:text-7xl leading-none mt-2 mb-4">Certificate of Completion</h1>
        <div className="gold-divider w-40 mx-auto my-8" />
        <p className="text-neutral-400 max-w-lg mx-auto font-body">
          Once you complete every lesson across all 8 levels, you become eligible to download
          your personal, watermarked Boardroom certificate — signed, sealed, and stamped with your name.
          Every certificate carries a unique verification code with a public share link so recruiters can
          confirm authenticity in one click.
        </p>
        <button onClick={dl} disabled={busy} className="btn-gold mt-10" data-testid="cert-download">
          <Download size={13} className="inline mr-2" /> {busy ? "Issuing…" : "Issue My Certificate"}
        </button>

        {cert && (
          <div className="mt-14 border border-[#D4AF37]/40 bg-[#0a0a0a] p-8 text-left" data-testid="cert-share-card">
            <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
              <ShieldCheck size={16} />
              <p className="overline">Your Verification Details</p>
            </div>
            <p className="font-display text-3xl mt-2">Public Verify Link</p>
            <p className="text-neutral-500 text-sm font-body mt-2 mb-6">
              Share this URL on LinkedIn, in your CV, or with recruiters. Anyone who opens it sees only
              your name, the issue date, and a Boardroom seal — no course details, no personal data.
            </p>
            <div className="flex items-center gap-3 border border-[#1f1f1f] bg-[#050505] px-4 py-3 mb-4">
              <LinkIcon size={14} className="text-[#D4AF37] flex-shrink-0" />
              <span className="font-mono text-sm text-neutral-200 truncate flex-1" data-testid="cert-share-url">
                {shareUrl}
              </span>
              <button onClick={copyLink} className="btn-outline-gold text-xs" data-testid="cert-copy-link">
                <Copy size={11} className="inline mr-1" /> Copy
              </button>
            </div>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer"
               className="btn-outline-gold inline-block" data-testid="cert-preview-link">
              Preview verify page ↗
            </a>
            <p className="text-[10px] uppercase tracking-widest text-neutral-600 mt-6">
              Certificate code · <span className="font-mono text-[#D4AF37]">{cert.code}</span>
            </p>
          </div>
        )}

        <p className="text-xs text-neutral-600 mt-6">
          If not eligible yet, you&apos;ll see how many lessons remain.
        </p>
      </div>
    </Layout>
  );
}
